/**
 * Sprint Redesign v1 (Do 03.09.2026) — /andere-verstehen (Phase 2).
 * Baustein 2 (Do 03.09.2026 15:15): Tier-Erkennung Bug-Fix + Steckbriefe.
 *   - Pro-Solo: FokustypSteckbriefe mit voller Tiefe (Erkennung + Tonalität + Impulse + Mimik + Wiedererkennungs-Frage)
 *     Erstauslieferung: Hund + Eule vollständig, Katze + Biene Platzhalter (Schritt 2 folgt).
 *   - Free: FreeFokustypBibliothek mit Vorschau + „Mehr Tiefe mit Pro Solo"-CTA
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import FreeFokustypBibliothek from "@/components/FreeFokustypBibliothek";
import FokustypSteckbriefe from "@/components/redesign/FokustypSteckbriefe";
import { isPremium } from "@/lib/licensing";
import { pickProSoloCopy } from "@/lib/proTrialCopy";

export default function PageAndereVerstehen() {
  const navigate = useNavigate();
  return (
    <RedesignLayout>
      {({ data }) => {
        const user = data?.user || {};
        const fokustyp = user.primary_fokustyp || user.fokustyp_primary || null;
        const pro = isPremium(user);
        const copy = pickProSoloCopy(user);
        return (
          <div
            className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8"
            data-testid="page-andere-verstehen"
            data-tier={pro ? "pro" : "free"}
          >
            <header>
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                Phase 2
              </p>
              <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Mein Gegenüber
              </h1>
              <p className="mt-2 text-slate-600 dark:text-slate-300 max-w-2xl">
                Andere erkennen und verstehen. Fokustyp-Radar, erste Impulse, Tonalität — hier liest du, wer da vor dir steht.
              </p>
            </header>

            {pro ? (
              <FokustypSteckbriefe primaryFokustyp={fokustyp} />
            ) : (
              <>
                <FreeFokustypBibliothek
                  primaryFokustyp={fokustyp}
                  onUpgradeClick={() => navigate("/pricing")}
                />
                <aside
                  className="rounded-2xl border-2 border-[var(--fts-primary-bright)] bg-white dark:bg-slate-900 p-5"
                  data-testid="andere-verstehen-upgrade-cta"
                >
                  <div className="flex items-start gap-2">
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                      Mehr Tiefe mit Pro Solo
                    </p>
                    {copy.isTrialEligible && (
                      <span
                        data-testid="andere-verstehen-trial-badge"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider"
                      >
                        <Sparkles className="w-3 h-3" /> 7 Tage · 1 €
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                    Sieh alle vier Steckbriefe mit Tonalität, ersten Impulsen, Mimik-Mustern und Wiedererkennungs-Frage.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/pricing")}
                    data-testid="andere-verstehen-upgrade-btn"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-4 py-2.5"
                  >
                    {copy.ctaLabel} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {copy.priceLine}
                  </p>
                </aside>
              </>
            )}
          </div>
        );
      }}
    </RedesignLayout>
  );
}
